import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ShieldCheck, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useApp } from '../context/AppContext';

// The company client gateway. Deliberately a door onto the same shop rather
// than a parallel site: signing in widens the catalogue and the collection
// points, and switches the checkout to invoicing. One menu to keep correct.
export default function CompanyPage() {
  const { t, company, isCompany, companyLogin, companyLogout } = useApp();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      await companyLogin(code.trim());
      navigate('/?tab=week2');
    } catch (ex) {
      const status = ex?.response?.status;
      setErr(status === 429
        ? (ex?.response?.data?.detail || 'Trop de tentatives.')
        : t('company_bad_code'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf9f5]">
      <Header />
      <div className="max-w-[520px] mx-auto px-5 py-16">
        <div className="bg-white rounded-2xl border border-neutral-200 p-7">
          <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-[#eef0e6]">
              <Building2 size={20} className="text-[#4b5e34]" />
            </span>
            <h1 className="font-display text-2xl text-[#2f3826]">{t('company_login')}</h1>
          </div>

          {isCompany ? (
            <>
              <p className="text-sm text-neutral-600 mb-5">
                {t('company_signed_in')} <b className="text-[#2f3826]">{company?.name}</b>.
              </p>
              <p className="flex items-start gap-2 text-sm text-[#2f3826] bg-[#f6f4ec] border border-[#d8dcc6] rounded-lg px-3 py-2.5 mb-5">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[#4b5e34]" />
                <span>{t('company_extra_note')}</span>
              </p>
              <button onClick={() => navigate('/?tab=week2')} data-testid="company-go-menu"
                      className="w-full flex items-center justify-center gap-2 bg-[#4b5e34] hover:bg-[#3f5230] text-white py-3 rounded-lg font-medium transition-colors">
                {t('order_next_week')} <ArrowRight size={16} />
              </button>
              <button onClick={companyLogout} data-testid="company-logout"
                      className="mt-3 w-full flex items-center justify-center gap-2 border border-neutral-300 text-neutral-700 py-2.5 rounded-lg text-sm hover:bg-neutral-100 transition-colors">
                <LogOut size={15} /> {t('company_signout')}
              </button>
            </>
          ) : (
            <form onSubmit={submit}>
              <p className="text-sm text-neutral-600 mb-5">{t('company_login_intro')}</p>
              <label className="block text-xs font-medium text-neutral-500 mb-1.5" htmlFor="company-code">
                {t('company_code')}
              </label>
              <input
                id="company-code"
                data-testid="company-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="off"
                spellCheck={false}
                placeholder="XXXX-XXXX-XXXX"
                className="w-full border border-neutral-300 rounded-lg px-3 py-2.5 text-sm tracking-[0.15em] uppercase font-mono focus:outline-none focus:ring-2 focus:ring-[#4b5e34]/30 focus:border-[#4b5e34]"
              />
              {err && <p data-testid="company-login-error" className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
              <button type="submit" disabled={busy || !code.trim()} data-testid="company-login-submit"
                      className="mt-4 w-full bg-[#4b5e34] hover:bg-[#3f5230] disabled:opacity-40 text-white py-3 rounded-lg font-medium transition-colors">
                {t('company_enter')}
              </button>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
